import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  MenuItem,
  Menu,
  FormControlLabel,
  Tooltip,
  Typography,
  ClickAwayListener,
  Checkbox,
  Box,
  Chip
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import SettingsIcon from '@material-ui/icons/Settings';

import { withStyles, MuiThemeProvider } from '@material-ui/core/styles';
import { lightThemeV2 } from '../../settings/themes/lightThemeV2';
import DownloadCenterPopover from './DownloadCenterPopover';
import UserMenuPopover from './UserMenuPopover';
// Load images
import capLogo from '../../assets/images/logo.svg';

// Styles
import styles from './styles';

import { headerMenu } from '../../utils/constants';
import { checkAuthState, getCurrentUser, getPermissions } from '../../utils/auth';
import { storageGetData, storageSetData } from '../../utils/storage';
import { actionCreators } from '../../containers/Auth/ducks/reducer';
import { actionCreators as authActions } from '../../redux/auth/actions';
import { withI18NContext } from '../i18n/i18nWrapper';

const Auth = require('j-toker');
const PubSub = require('pubsub-js');

const isBeforeDate = date => {
  const today = new Date();

  return today < date;
};

const SHOW_NEW_FEATURE = isBeforeDate(new Date(2025, 6, 30));

// Clients allowed to see nested menu submenus (e.g. Ejecutivo → Mapa/Dashboard).
const CLIENT_IDS_ALLOWED_FOR_SUBMENUS = [1];

class Header extends Component {
  static propTypes = {
    classes: PropTypes.object,
    currentPath: PropTypes.string,
    // Auth action creator to reset values when logout.
    logout: PropTypes.func,
    changeLocale: PropTypes.func,
    pathname: PropTypes.string
  };

  state = {
    hasLogout: false,
    anchorEl: null,
    mobileMoreAnchorEl: null,
    showOperativoTooltip: true,
    hoveredMenu: null
  };

  // Delay before closing the submenu so a brief mouse slip doesn't collapse it before the user reaches the target.
  SUBMENU_CLOSE_DELAY_MS = 500;

  handleMenuMouseEnter = name => {
    clearTimeout(this.closeSubmenuTimer);
    this.setState({ hoveredMenu: name });
  };

  handleMenuMouseLeave = () => {
    clearTimeout(this.closeSubmenuTimer);
    this.closeSubmenuTimer = setTimeout(() => this.setState({ hoveredMenu: null }), this.SUBMENU_CLOSE_DELAY_MS);
  };

  // Returns the submenu children for a menu entry only when the current client
  // is allowed to see nested submenus; otherwise the menu behaves as a single option.
  menuChildren = name => {
    const { currentUser } = this.props;
    const clientId = currentUser && currentUser.client_id;

    if (!CLIENT_IDS_ALLOWED_FOR_SUBMENUS.includes(clientId)) return null;

    const entry = headerMenu[name];

    return entry && entry.children && entry.children.length ? entry.children : null;
  };

  componentDidMount() {
    const hideOperativoTooltip = storageGetData('hideOperativoTooltip', null);

    this.setState({ showOperativoTooltip: !hideOperativoTooltip });
  }

  componentWillUnmount() {
    clearTimeout(this.closeSubmenuTimer);
  }

  handleLogout = () => {
    const { logout } = this.props;

    Auth.signOut();

    PubSub.subscribe('auth.signOut.success', () => {
      logout();
    });
  };

  handleProfileMenuOpen = event => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleMenuClose = () => {
    this.setState({ anchorEl: null });
    this.handleMobileMenuClose();
  };

  handleMobileMenuOpen = event => {
    this.setState({ mobileMoreAnchorEl: event.currentTarget });
  };

  handleMobileMenuClose = () => {
    this.setState({ mobileMoreAnchorEl: null });
  };

  canViewAdmin = currentUser => !!['super_admin', 'admin', 'supervisor', 'facilitator'].find(type => currentUser.user_type === type);

  canViewOrders = currentUser => currentUser.user_type !== 'visializator';

  menuSelectedClass = path => {
    const { classes, currentPath } = this.props;

    return path === `/${currentPath.split('/')[1]}` ? ` ${classes.menu}__menu__item_selected` : '';
  };

  apiMenuName = name => {
    const { currentUser } = this.props;

    return name === 'Operativo' ? currentUser.client_project_alias || 'Proyectos' : name;
  };

  apiMenuNameKey = name => {
    const { currentUser } = this.props;
    const i18nKeys = {
      Operativo: 'menu.operative',
      Ejecutivo: 'menu.executive',
      Analítica: 'menu.analitics'
    };

    return name === 'Operativo' ? currentUser.client_project_alias || i18nKeys[name] : i18nKeys[name];
  };

  hideMenuBadge = name => name !== 'Operativo';

  dontShowTooltip = () => {
    storageSetData('hideOperativoTooltip', true);
  };

  renderTooltip = () => {
    const { classes } = this.props;

    return (
      <Fragment>
        <Typography color="secondary">Importante!</Typography>
        <Typography>
          Ahora el menu{' '}
          <Box component="span" fontWeight="fontWeightBold">
            Operativo
          </Box>{' '}
          ha cambiado a{' '}
          <Box component="span" fontWeight="fontWeightBold">
            {this.apiMenuName('Operativo')}
          </Box>
          , para brindar una mejor experiencia de uso.
        </Typography>
        <FormControlLabel
          label=<FormattedMessage id="infoDialog.index.dontShowAgain" defaultMessage="No volver a mostrar" />
          control={<Checkbox color="secondary" className={`${classes.menu}__tooltip`} onChange={this.dontShowTooltip}></Checkbox>}
        />
      </Fragment>
    );
  };

  handleTooltipClose = () => this.setState({ showOperativoTooltip: false });

  handleChangeLocale = event => {
    const { currentUser, i18nContext, changeLocale } = this.props;
    const newLocale = event.target.value;

    changeLocale(currentUser.client_id, currentUser.id, newLocale);
  };

  renderSubmenu = () => {
    const { classes, pathname } = this.props;
    const { hoveredMenu } = this.state;
    const children = hoveredMenu && this.menuChildren(hoveredMenu);

    if (!children || !children.length) return null;

    return (
      <MuiThemeProvider theme={lightThemeV2}>
        <AppBar position="static" className={classes.submenuBar} onMouseEnter={() => this.handleMenuMouseEnter(hoveredMenu)}>
          <Toolbar className={classes.submenuToolbar}>
            {children.map(child => {
              const isSelected = pathname === child.path;

              return (
                <Link to={child.path} key={`submenu-${child.path}`}>
                  <Button className={`${classes.submenuItem} ${isSelected ? classes.submenuItem_selected : ''}`}>
                    <FormattedMessage id={child.i18nKey} defaultMessage={child.name} />
                  </Button>
                </Link>
              );
            })}
          </Toolbar>
        </AppBar>
      </MuiThemeProvider>
    );
  };

  render() {
    const { classes, permissions, currentUser, i18nContext } = this.props;
    const menuPermissions = permissions
      ? permissions.filter(p => p.permission_action.name === 'View' && p.permission.permission_type === 'menu' && p.can)
      : [];
    const { mobileMoreAnchorEl, showOperativoTooltip } = this.state;
    const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
    const isAdmin = this.canViewAdmin(currentUser);
    const hasOrders = this.canViewOrders(currentUser);
    const renderMobileMenu = (
      <Menu
        anchorEl={mobileMoreAnchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={isMobileMenuOpen}
        onClose={this.handleMenuClose}
      >
        <MenuItem onClick={this.handleMobileMenuClose}>
          <Link to={'/beneficiaries'}>
            <Button color="primary" className={`${classes.menu}__menu__item ${this.menuSelectedClass('/beneficiaries')}`}>
              <FormattedMessage id="menu.contacts" />
            </Button>
          </Link>
        </MenuItem>
        {false && isAdmin && (
          <MenuItem onClick={this.handleMobileMenuClose}>
            <Link to={'/formulary-reports'}>
              <Button color="primary" className={`${classes.menu}__menu__item ${this.menuSelectedClass('/formulary-reports')}`}>
                <FormattedMessage id="menu.reports" defaultMessage="Reportes" />
              </Button>
            </Link>
          </MenuItem>
        )}
        {menuPermissions.map(menu => {
          const entry = headerMenu[menu.permission.name];
          const children = this.menuChildren(menu.permission.name);

          if (children && children.length) {
            return [
              <MenuItem disabled key={`mobile-menu-${menu.permission.id}`}>
                <Button color="primary" className={`${classes.menu}__menu__item`}>
                  {this.apiMenuName(menu.permission.name)}
                </Button>
              </MenuItem>,
              ...children.map(child => (
                <MenuItem onClick={this.handleMobileMenuClose} key={`mobile-submenu-${child.path}`} style={{ paddingLeft: 32 }}>
                  <Link to={child.path}>
                    <Button color="primary" className={`${classes.menu}__menu__item ${this.menuSelectedClass(child.path)}`}>
                      <FormattedMessage id={child.i18nKey} defaultMessage={child.name} />
                    </Button>
                  </Link>
                </MenuItem>
              ))
            ];
          }

          return (
            <MenuItem onClick={this.handleMobileMenuClose} key={`mobile-menu-${menu.permission.id}`}>
              <Link to={entry.path}>
                <Button color="primary" className={`${classes.menu}__menu__item ${this.menuSelectedClass(entry.path)}`}>
                  {this.apiMenuName(menu.permission.name)}
                </Button>
              </Link>
            </MenuItem>
          );
        })}
        {hasOrders && (
          <MenuItem onClick={this.handleMobileMenuClose}>
            <Link to={'/work-orders'}>
              <Button color="primary" className={`${classes.menu}__menu__item ${this.menuSelectedClass('/work-orders')}`}>
                <FormattedMessage id="menu.work-orders" defaultMessage="Ordenes de Trabajo" />
              </Button>
            </Link>
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem onClick={this.handleMobileMenuClose}>
            <Link to={'/admin'}>
              <Button color="primary" className={`${classes.menu}__menu__item ${this.menuSelectedClass('/admin')}`}>
                <FormattedMessage id="menu.config" defaultMessage="Configuración" />
              </Button>
            </Link>
          </MenuItem>
        )}
        <MenuItem>
          <DownloadCenterPopover menuItemClassName={`${classes.menu}__menu__item`} clientId={currentUser.client_id} />
        </MenuItem>
        <MenuItem onClick={this.handleMobileMenuClose}>
          <Button onClick={this.handleLogout} color="primary" className={`${classes.menu}__menu__item`}>
            Logout
          </Button>
        </MenuItem>
      </Menu>
    );

    const renderMenu = (
      <>
        <Link to={'/beneficiaries'} onMouseEnter={this.handleMenuMouseLeave}>
          <Button color="primary" className={`${classes.menu}__menu__item ${this.menuSelectedClass('/beneficiaries')}`}>
            <FormattedMessage id="menu.contacts" defaultMessage="Contactos" />
          </Button>
        </Link>
        {false && isAdmin && (
          <Link to={'/formulary-reports'}>
            <Button color="primary" className={`${classes.menu}__menu__item ${this.menuSelectedClass('/formulary-reports')}`}>
              <FormattedMessage id="menu.reports" defaultMessage="Reportes" />
            </Button>
          </Link>
        )}
        {hasOrders && (
          <Link to={'/reports'} onMouseEnter={this.handleMenuMouseLeave}>
            <Button color="primary" className={`${classes.menu}__menu__item ${this.menuSelectedClass('/reports')}`}>
              <FormattedMessage id="reports.header.title" defaultMessage="Reportes de Formulario" />
            </Button>
          </Link>
        )}
        {menuPermissions.map(menu => (
          <ClickAwayListener onClickAway={this.handleTooltipClose} key={`menu-${menu.permission.id}`}>
            <Tooltip
              title={this.renderTooltip()}
              arrow
              interactive
              open={false && showOperativoTooltip && menu.permission.name === 'Operativo'}
              classes={{ tooltip: classes.tooltip }}
            >
              <Link
                to={headerMenu[menu.permission.name].path}
                onMouseEnter={() =>
                  this.menuChildren(menu.permission.name) ? this.handleMenuMouseEnter(menu.permission.name) : this.handleMenuMouseLeave()
                }
              >
                <Button
                  color="primary"
                  className={`${classes.menu}__menu__item ${this.menuSelectedClass(headerMenu[menu.permission.name].path)}`}
                >
                  <FormattedMessage
                    id={this.apiMenuNameKey(menu.permission.name)}
                    defaultMessage={this.apiMenuName(menu.permission.name)}
                  />
                </Button>
              </Link>
            </Tooltip>
          </ClickAwayListener>
        ))}
        <Link to={'/inspections'} onMouseEnter={this.handleMenuMouseLeave}>
          <Button color="primary" className={`${classes.menu}__menu__item ${this.menuSelectedClass('/inspections')}`}>
            <FormattedMessage id="project.inspection.inspections" defaultMessage="Inspecciones" />
            {SHOW_NEW_FEATURE && <Chip className={classes.newFeature} label={<FormattedMessage id="common.new" />} size="small" />}
          </Button>
        </Link>
        {hasOrders && (
          <Link to={'/work-orders'} onMouseEnter={this.handleMenuMouseLeave}>
            <Button color="primary" className={`${classes.menu}__menu__item ${this.menuSelectedClass('/work-orders')}`}>
              <FormattedMessage id="menu.work-orders" defaultMessage="Ordenes de Trabajo" />
            </Button>
          </Link>
        )}
      </>
    );

    return (
      <div className={classes.root} onMouseLeave={this.handleMenuMouseLeave}>
        <AppBar position="static" className={classes.root}>
          <Toolbar className={`${classes.root}__toolbar`}>
            {!!currentUser.client_logo && <img className={classes.client_logo} src={currentUser.client_logo} alt="Logo" />}
            <img src={capLogo} alt="Logo" className={classes.cap_logo} />
            <div className={classes.grow} />
            <div className={classes.sectionDesktop}>
              {renderMenu}

              <MuiThemeProvider theme={lightThemeV2}>
                <div onMouseEnter={this.handleMenuMouseLeave}>
                  <DownloadCenterPopover menuItemClassName={`${classes.menu}__menu__action`} clientId={currentUser.client_id} />
                </div>
                {isAdmin && (
                  <div onMouseEnter={this.handleMenuMouseLeave}>
                    <Link to={'/admin'}>
                      <Tooltip title={<FormattedMessage id="menu.config" defaultMessage="Configuración" />}>
                        <IconButton
                          color="primary"
                          aria-haspopup="false"
                          className={`${classes.menu}__menu__action${this.menuSelectedClass('/admin')}`}
                        >
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Link>
                  </div>
                )}
                <div onMouseEnter={this.handleMenuMouseLeave}>
                  <UserMenuPopover
                    menuItemClassName={`${classes.menu}__menu__action`}
                    currentUser={currentUser}
                    locale={i18nContext.locale}
                    onChangeLocale={this.handleChangeLocale}
                    onLogout={this.handleLogout}
                  />
                </div>
              </MuiThemeProvider>
            </div>
            <div className={classes.sectionMobile}>
              <IconButton aria-haspopup="true" onClick={this.handleMobileMenuOpen} color="primary">
                <MenuIcon />
              </IconButton>
            </div>
          </Toolbar>
        </AppBar>
        {this.renderSubmenu()}
        {renderMobileMenu}
      </div>
    );
  }
}

// Redux connection
export default connect(
  state => ({
    currentUser: getCurrentUser(state),
    isLoggedIn: checkAuthState(state),
    permissions: getPermissions(state),
    pathname: state.router && state.router.location ? state.router.location.pathname : ''
  }),
  {
    logout: actionCreators.logout,
    changeLocale: authActions.changeLocale
  }
)(withI18NContext(withStyles(styles)(Header)));
